import type { LicenseDetailDto, LicenseHistoryDto, LicenseInfoDto } from '@dbrain/shared';
import { Alert, Badge, Button, Icons, Skeleton, type BadgeTone } from '@dbrain/ui';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useCompanyZone } from '../../api/hooks.js';
import { isDetail, useLicense } from '../../api/license.js';
import { DataTable, type Column } from '../../components/admin/DataTable.js';
import { ErrorAlert } from '../../components/ErrorAlert.js';
import { LicenseUploadDialog } from '../../components/license/LicenseUploadDialog.js';
import { ProductTags } from '../../components/license/parts.js';
import { daysBetween, licenseDay, licenseDayAt, licenseRange } from '@dbrain/app-core/license-days';
import { AdminLayout } from './AdminLayout.js';

/**
 * Yönetim → Lisans (YonetimLisans, LisansYukle; Phase 13f, 06 §26.4): the license's state with the days left and
 * its term, the details with this installation's id to send to the vendor, what happens when it ends, and every
 * license installed. Super admins and the break-glass admin only (D-126); the upload checks a file first. In the
 * free trial and right after it (LisansDeneme, 13f-2, D-133): the trial's card, the three steps to carry on, what
 * happens when it ends, and its row in the history; the header's button is the only upload on the page.
 */
type StatusKey = 'active' | 'reminder' | 'grace' | 'read_only' | 'none';

const TONE: Record<StatusKey, 'success' | 'warning' | 'danger'> = { active: 'success', reminder: 'warning', grace: 'warning', read_only: 'danger', none: 'danger' };

function StatusCard({ detail }: { detail: LicenseDetailDto }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const current = detail.current;
  // the free trial has its own card (TrialCard)
  const key: StatusKey = current && detail.status !== 'trial' ? detail.status : 'none';
  const tone = TONE[key];
  const days = detail.daysLeft ?? 0;
  // the term's share gone, by the day the API judged (a queued license's days are not this one's)
  const total = current ? daysBetween(current.validFrom, current.validUntil) + 1 : 0;
  const leftInThis = current && detail.endsOn && (key === 'active' || key === 'reminder') ? days - daysBetween(current.validUntil, detail.endsOn) : 0;
  const gone = total > 0 ? Math.min(1, Math.max(0, (total - leftInThis) / total)) : 1;
  const Icon = key === 'active' ? Icons.check : key === 'read_only' || key === 'none' ? Icons.lock : Icons.time;
  const line =
    key === 'reminder'
      ? t('license.page.renew')
      : key === 'grace' && detail.endsOn && detail.readOnlyFrom
        ? t('license.page.graceLine', { ended: licenseDayAt(detail.endsOn, locale), day: licenseDayAt(detail.readOnlyFrom, locale) })
        : key === 'read_only'
          ? t('license.page.readOnlyLine')
          : key === 'none'
            ? t('license.page.noneLine')
            : null;
  return (
    <section className={`admin-card license-status license-status--${tone}`} data-testid="license-status" data-status={key}>
      <div className="license-status__head">
        <span className="license-status__icon" aria-hidden="true">
          <Icon size={22} />
        </span>
        <div className="license-status__text">
          <h2 className="license-status__title">{t(`license.page.status.${key}`)}</h2>
          {current ? (
            <p className="license-status__sub">
              {current.customer} · {t(current.kind === 'trial' ? 'license.page.kindTrial' : 'license.page.kindFull')}
            </p>
          ) : null}
          {line ? <p className="license-status__line">{line}</p> : null}
        </div>
        {(key === 'active' || key === 'reminder' || key === 'grace') && detail.endsOn ? (
          <div className="license-status__days">
            <strong data-testid="license-days">{t('license.page.days', { count: days })}</strong>
            <span>
              {key === 'grace' && detail.readOnlyFrom
                ? t('license.page.graceLeft', { day: licenseDay(detail.readOnlyFrom, locale) })
                : t('license.page.left', { day: licenseDay(detail.endsOn, locale) })}
            </span>
          </div>
        ) : null}
      </div>
      {current ? (
        <>
          <svg className="license-term" viewBox="0 0 1000 6" preserveAspectRatio="none" role="img" aria-label={t('license.page.termLabel', { percent: Math.round(gone * 100) })} data-testid="license-term">
            <rect x="0" y="0" width="1000" height="6" rx="3" className="license-term__track" />
            <rect x="0" y="0" width={Math.max(6, gone * 1000)} height="6" rx="3" className={`license-term__fill license-term__fill--${tone}`} />
          </svg>
          <div className="license-term__ends">
            <span>{t('license.page.start', { day: licenseDay(current.validFrom, locale) })}</span>
            <span>{t('license.page.end', { day: licenseDay(current.validUntil, locale) })}</span>
          </div>
        </>
      ) : null}
      {detail.next ? (
        <p className="license-status__next" data-testid="license-next">
          <Icons.info size={16} />
          {t('license.page.next', { id: detail.next.licenseId, day: licenseDayAt(detail.next.validFrom, locale, true) })}
        </p>
      ) : null}
    </section>
  );
}

/** The free trial (LisansDeneme): blue while it runs (amber in its last 7 days), red once it ran out. */
function TrialCard({ detail }: { detail: LicenseDetailDto }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const trial = detail.trial;
  if (!trial) return null;
  const running = detail.status === 'trial';
  const days = detail.daysLeft ?? 0;
  const late = running && detail.reminderFrom !== null && days <= 7;
  const tone = running ? (late ? 'warning' : 'info') : 'danger';
  const total = daysBetween(trial.from, trial.until) + 1;
  // the trial's own days gone (a license taking over earlier does not shorten the bar)
  const left = running && detail.endsOn ? days + daysBetween(detail.endsOn, trial.until) : 0;
  const gone = Math.min(1, Math.max(0, (total - left) / total));
  const next = detail.next;
  return (
    <section className={`admin-card license-status license-status--${tone}`} data-testid="license-status" data-status={detail.status} data-tone={tone}>
      <div className="license-status__head">
        <span className="license-status__icon" aria-hidden="true">
          {running ? <Icons.gift size={22} /> : <Icons.lock size={22} />}
        </span>
        <div className="license-status__text">
          <h2 className="license-status__title">{t(running ? 'license.page.status.trial' : 'license.page.status.trialEnded')}</h2>
          {running ? (
            <p className="license-status__sub">{t(next ? 'license.page.trialSubQueued' : 'license.page.trialSub')}</p>
          ) : (
            <p className="license-status__line">{t('license.page.trialEndedLine')}</p>
          )}
        </div>
        {running && detail.endsOn ? (
          <div className="license-status__days">
            <strong data-testid="license-days">{t('license.page.days', { count: days })}</strong>
            <span>{t('license.page.left', { day: licenseDay(detail.endsOn, locale) })}</span>
          </div>
        ) : null}
      </div>
      <svg className="license-term" viewBox="0 0 1000 6" preserveAspectRatio="none" role="img" aria-label={t('license.page.trialTermLabel', { percent: Math.round(gone * 100) })} data-testid="license-term">
        <rect x="0" y="0" width="1000" height="6" rx="3" className="license-term__track" />
        <rect x="0" y="0" width={Math.max(6, gone * 1000)} height="6" rx="3" className={`license-term__fill license-term__fill--${tone}`} />
      </svg>
      <div className="license-term__ends">
        <span>{t('license.page.installed', { day: licenseDay(trial.from, locale) })}</span>
        <span>{t('license.page.trialEnd', { day: licenseDay(trial.until, locale) })}</span>
      </div>
      {next ? (
        <p className="license-status__next" data-testid="license-next">
          <Icons.info size={16} />
          {running
            ? t(detail.readOnlyFrom ? 'license.page.trialNextGap' : 'license.page.trialNext', {
                id: next.licenseId,
                day: licenseDayAt(next.validFrom, locale, true),
                ends: licenseDayAt(trial.until, locale, true),
              })
            : t('license.page.next', { id: next.licenseId, day: licenseDayAt(next.validFrom, locale, true) })}
        </p>
      ) : null}
    </section>
  );
}

function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);
  return (
    <Button
      size="sm"
      variant="secondary"
      icon={copied ? <Icons.check size={14} /> : <Icons.copy size={14} />}
      onClick={() => {
        void navigator.clipboard
          ?.writeText(text)
          .then(() => setCopied(true))
          .catch(() => setCopied(false));
      }}
      data-testid="license-copy"
    >
      {copied ? t('license.page.copied') : t('license.page.copy')}
    </Button>
  );
}

/** "Denemeden sonra devam etmek için": this installation's id to send, the vendor's license, the upload above. */
function ContinueCard({ installationId }: { installationId: string }) {
  const { t } = useTranslation();
  return (
    <section className="admin-card license-continue" data-testid="license-continue">
      <h2 className="license-card__title">{t('license.page.continue.title')}</h2>
      <ol className="license-continue__steps">
        <li className="license-continue__step">
          <span className="license-continue__number" aria-hidden="true">
            1
          </span>
          <span>
            <strong>{t('license.page.continue.copyTitle')}</strong>
            <span className="license-continue__id">
              <code className="license-installation__code" data-testid="license-installation">
                {installationId}
              </code>
              <CopyButton text={installationId} />
            </span>
            <span className="license-continue__text">{t('license.page.continue.copyText')}</span>
          </span>
        </li>
        <li className="license-continue__step">
          <span className="license-continue__number" aria-hidden="true">
            2
          </span>
          <span>
            <strong>{t('license.page.continue.prepareTitle')}</strong>
            <span className="license-continue__text">{t('license.page.continue.prepareText')}</span>
          </span>
        </li>
        <li className="license-continue__step">
          <span className="license-continue__number" aria-hidden="true">
            3
          </span>
          <span>
            <strong>{t('license.page.continue.uploadTitle')}</strong>
            <span className="license-continue__text">
              <Trans i18nKey="license.page.continue.uploadText" components={{ b: <strong /> }} />
            </span>
          </span>
        </li>
      </ol>
    </section>
  );
}

function DetailsCard({ detail, license }: { detail: LicenseDetailDto; license: LicenseInfoDto | null }) {
  const { t, i18n } = useTranslation();
  return (
    <section className="admin-card license-details" data-testid="license-details">
      <h2 className="license-card__title">{t('license.page.details')}</h2>
      {license ? (
        <dl className="license-facts">
          <div>
            <dt>{t('license.facts.owner')}</dt>
            <dd>{license.customer}</dd>
          </div>
          <div>
            <dt>{t('license.facts.products')}</dt>
            <dd>
              <ProductTags products={license.products} />
            </dd>
          </div>
          <div>
            <dt>{t('license.facts.validity')}</dt>
            <dd>{licenseRange(license.validFrom, license.validUntil, i18n.language)}</dd>
          </div>
          <div>
            <dt>{t('license.facts.licenseId')}</dt>
            <dd className="license-mono" data-testid="license-id">
              {license.licenseId}
            </dd>
          </div>
        </dl>
      ) : null}
      <div className={license ? 'license-installation' : 'license-installation license-installation--only'}>
        <div className="license-installation__id">
          <span className="license-installation__label">{t('license.facts.installation')}</span>
          <span className="license-installation__row">
            <code className="license-installation__code" data-testid="license-installation">
              {detail.installationId}
            </code>
            <CopyButton text={detail.installationId} />
          </span>
        </div>
        <p className="license-installation__note">{t('license.page.boundNote')}</p>
      </div>
    </section>
  );
}

/** "Süre bitince ne olur?" for a license; "Deneme bitince ne olur?" for the free trial (no grace, once only). */
function WhatHappens({ trial = false }: { trial?: boolean }) {
  const { t } = useTranslation();
  const base = trial ? 'license.page.trialEnds' : 'license.page.ends';
  const steps = trial ? (['first', 'last', 'readOnly'] as const) : (['reminder', 'grace', 'readOnly'] as const);
  return (
    <section className="admin-card license-ends" data-testid="license-what-happens">
      <div className="license-ends__head">
        <h2 className="license-card__title">{t(`${base}.title`)}</h2>
        <span className="license-ends__safe">
          <Icons.check size={16} />
          {t('license.page.ends.noDelete')}
        </span>
      </div>
      <ol className="license-ends__steps">
        {steps.map((step, index) => (
          <li key={step} className={`license-ends__step license-ends__step--${step}`}>
            <span className="license-ends__number" aria-hidden="true">
              {index + 1}
            </span>
            <span>
              <strong>{t(`${base}.${step}Title`)}</strong>
              <span className="license-ends__text">{t(`${base}.${step}`)}</span>
            </span>
          </li>
        ))}
      </ol>
      <p className="license-ends__foot">{t(`${base}.foot`)}</p>
    </section>
  );
}

const STATE_TONE: Record<LicenseHistoryDto['state'], BadgeTone> = { trial: 'info', active: 'success', queued: 'info', ended: 'neutral', invalid: 'danger' };

function History({ rows }: { rows: LicenseHistoryDto[] }) {
  const { t, i18n } = useTranslation();
  const zone = useCompanyZone();
  const locale = i18n.language;
  // only the free trial so far: it was started, not uploaded (LisansDeneme)
  const onlyTrial = rows.length > 0 && rows.every((row) => row.kind === 'free_trial');
  const columns: Column<LicenseHistoryDto>[] = [
    {
      id: 'uploaded',
      header: t(onlyTrial ? 'license.page.history.started' : 'license.page.history.uploaded'),
      width: 'md',
      cell: (row) =>
        row.kind === 'free_trial'
          ? licenseDay(row.validFrom, locale)
          : new Date(row.uploadedAt).toLocaleDateString(locale.startsWith('tr') ? 'tr' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: zone }),
    },
    { id: 'by', header: t(onlyTrial ? 'license.page.history.startedBy' : 'license.page.history.by'), width: 'sm', cell: (row) => row.uploadedBy ?? t('license.page.history.tool') },
    { id: 'id', header: t('license.facts.licenseId'), width: 'md', cell: (row) => <span className="license-mono">{row.licenseId || '—'}</span> },
    {
      id: 'validity',
      header: t('license.facts.validity'),
      cell: (row) =>
        `${licenseRange(row.validFrom, row.validUntil, locale)}${row.kind === 'trial' ? ` · ${t('license.page.history.trial')}` : row.kind === 'free_trial' ? ` · ${t('license.page.history.freeTrial')}` : ''}`,
    },
    { id: 'products', header: t('license.facts.products'), width: 'xl', cell: (row) => <ProductTags products={row.products} /> },
    {
      id: 'state',
      header: t('license.page.history.state'),
      width: 'sm',
      cell: (row) => (
        <Badge tone={STATE_TONE[row.state]} className={row.state === 'active' || row.state === 'trial' ? `license-state--${row.state}` : undefined}>
          {row.kind === 'free_trial' && row.state === 'ended' ? t('license.page.history.trialEnded') : t(`license.page.history.states.${row.state}`)}
        </Badge>
      ),
    },
  ];
  return (
    <section className="admin-card license-history">
      <h2 className="license-card__title">{t('license.page.history.title')}</h2>
      <DataTable
        label={t('license.page.history.title')}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.licenseId || 'free-trial'}
        rowTestId={() => 'license-history-row'}
        rowData={(row) => ({ 'data-state': row.state })}
        empty={t('license.page.history.empty')}
        data-testid="license-history"
      />
    </section>
  );
}

export function LicensePage() {
  const { t } = useTranslation();
  const license = useLicense();
  const [upload, setUpload] = useState(false);
  const [applied, setApplied] = useState<LicenseInfoDto | null>(null);
  const detail = isDetail(license.data) ? license.data : undefined;
  return (
    <AdminLayout
      section="license"
      title={t('admin.sections.license')}
      actions={
        <Button icon={<Icons.upload size={16} />} onClick={() => setUpload(true)} data-testid="license-upload">
          {t('license.upload.title')}
        </Button>
      }
    >
      <p className="license-intro">{t('license.page.intro')}</p>
      {/* Phase 15a (D-143): plain http on purpose, until a domain with https */}
      {detail?.plainHttp ? (
        <p className="license-plain-http" role="note" data-testid="license-plain-http">
          <Icons.warning size={14} aria-hidden="true" />
          <strong>{t('license.page.plainHttp')}</strong>
          <span>{t('license.page.plainHttpHint')}</span>
        </p>
      ) : null}
      {applied ? <Alert tone="success" title={t('license.upload.applied', { id: applied.licenseId })} data-testid="license-applied" /> : null}
      <ErrorAlert error={license.error} />
      {license.isPending ? <Skeleton lines={6} /> : null}
      {detail && detail.basis === 'trial' ? (
        <div className="license-page">
          <TrialCard detail={detail} />
          {detail.next ? <DetailsCard detail={detail} license={detail.next} /> : <ContinueCard installationId={detail.installationId} />}
          <WhatHappens trial />
          <History rows={detail.history} />
        </div>
      ) : detail ? (
        <div className="license-page">
          <StatusCard detail={detail} />
          <DetailsCard detail={detail} license={detail.current ?? detail.next} />
          <WhatHappens />
          <History rows={detail.history} />
        </div>
      ) : null}
      {upload ? <LicenseUploadDialog onClose={() => setUpload(false)} onApplied={setApplied} /> : null}
    </AdminLayout>
  );
}
